import React, { useState } from "react";
import Logo from "../../../assets/images/logo_admin.png";
import Laundry1 from "../../../assets/images/laundry-1.png";
import Laundry2 from "../../../assets/images/laundry-2.png";
import Laundry3 from "../../../assets/images/laundry-3.png";
import { useNavigate } from "react-router-dom";
import { authService } from "./auth.service";
import { LoginFormData } from "./auth.types";

export default function AdminLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const [errors, setErrors] = useState({
    email: "",
    password: "",
    general: "",
  });

  const validateForm = () => {
    let valid = true;
    const newErrors = { email: "", password: "", general: "" };

    if (!email.trim()) {
      newErrors.email = "Email is required";
      valid = false;
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      newErrors.email = "Enter a valid email";
      valid = false;
    }

    if (!password.trim()) {
      newErrors.password = "Password is required";
      valid = false;
    }

    setErrors(newErrors);
    return valid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Clear previous general error
    setErrors((prev) => ({ ...prev, general: "" }));

    if (validateForm()) {
      setIsLoading(true);

      try {
        const loginData = new LoginFormData(email, password);
        const response = await authService.login(loginData);

        if (response.status) {
          // Successfully logged in, navigate to dashboard
          navigate("/admin/vendor-form");
        } else {
          // Login failed, show error message
          setErrors((prev) => ({
            ...prev,
            general: response.data?.error || "Login failed. Please try again.",
          }));
        }
      } catch (error) {
        setErrors((prev) => ({
          ...prev,
          general: "An unexpected error occurred. Please try again.",
        }));
      } finally {
        setIsLoading(false);
      }
    }
  };

  return (
    <section className="grid w-full h-screen grid-cols-1 bg-white md:grid-cols-2">

      {/* LEFT SIDE (ILLUSTRATION) */}
      <div className="relative flex items-center justify-center bg-gradient-to-br from-[#F7F9FC] to-[#E9F3FF] overflow-hidden">

        {/* BACKGROUND BLURS */}
        <div className="absolute rounded-full w-80 h-80 bg-purple-300/30 blur-3xl -top-24 -left-12"></div>
        <div className="absolute bottom-0 right-0 rounded-full w-72 h-72 bg-blue-300/30 blur-3xl"></div>

        {/* MAIN CENTER IMAGE — WITH BORDER */}
        <div className="relative z-20">
          <div className="p-3 bg-white shadow-2xl rounded-2xl">
            <img
              src={Laundry1}
              alt="Center"
              className="w-full max-w-[420px] object-contain rounded-xl"
            />
          </div>

          {/* Glow behind image */}
          <div className="absolute inset-0 w-full h-full bg-white/10 blur-2xl rounded-xl"></div>
        </div>

        {/* TOP RIGHT IMAGE — BIGGER */}
        <div className="absolute z-20 top-14 right-10">
          <div className="p-2 bg-white shadow-2xl rounded-2xl rotate-6">
            <img
              src={Laundry2}
              alt="Laundry 2"
              className="w-[190px] h-[190px] rounded-xl object-cover"
            />
          </div>
        </div>

        {/* BOTTOM LEFT IMAGE — BIGGER */}
        <div className="absolute z-20 bottom-8 left-10">
          <div className="p-2 bg-white shadow-2xl rounded-2xl -rotate-6">
            <img
              src={Laundry3}
              alt="Laundry 3"
              className="w-[200px] h-[200px] rounded-xl object-cover"
            />
          </div>
        </div>

        {/* Decorative shapes — Tailwind Animation */}
        <div className="absolute top-1/2 left-[15%] w-36 h-36 border-[6px] border-[#FFBC7D] 
            rounded-full opacity-60 animate-floaty"></div>
        <div className="absolute top-[25%] right-[25%] w-24 h-24 border-[5px] border-[#9DE6FF] 
            rounded-full opacity-60 animate-floatyFast"></div>
        <div className="absolute bottom-[20%] left-[40%] w-44 h-44 border-[6px] border-[#FF9FC3] 
            rounded-full opacity-60 animate-floatySlow"></div>
      </div>

      {/* RIGHT SIDE (LOGIN FORM) */}
      <div className="flex justify-center items-center min-h-screen px-4">
        <div className="w-full max-w-md mt-[-50px]">

          {/* CONTENT WRAPPER WITH TOP PADDING to avoid overlapping logo */}
          <div className="pt-10">

            <div className="mt-5 mb-6 flex justify-center">
              <img src={Logo} alt="logo" className="w-20" />
            </div>

            {/* HEADING */}
            <h2 className="text-[32px] font-semibold text-gray-900 mb-1 text-center">
              Saaflo Admin Portal
            </h2>

            <p className="mb-8 text-[18px] text-gray-500 text-center">
              Secure access to your dashboard
            </p>

            {/* GENERAL ERROR MESSAGE */}
            {errors.general && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
                <p className="text-sm text-red-600">{errors.general}</p>
              </div>
            )}

            {/* FORM */}
            <form className="space-y-5" onSubmit={handleSubmit}>

              {/* EMAIL */}
              <div>
                <label className="block mb-1 text-sm font-medium text-gray-700">
                  Email Address
                </label>
                <input
                  type="text"
                  className={`w-full px-4 py-2 border rounded-md outline-none ${errors.email
                      ? "border-[#6a0dad] focus:ring-[#6c21b3]"
                      : "border-gray-300 focus:ring-2 focus:ring-[#6c21b3]"
                    }`}
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={isLoading}
                />
                {errors.email && (
                  <p className="mt-1 text-sm text-[#6a0dad]">{errors.email}</p>
                )}
              </div>

              {/* PASSWORD */}
              <div>
                <label className="block mb-1 text-sm font-medium text-gray-700">
                  Password
                </label>
                <input
                  type="password"
                  className={`w-full px-4 py-2 border rounded-md outline-none ${errors.password
                      ? "border-[#6a0dad] focus:ring-[#6c21b3]"
                      : "border-gray-300 focus:ring-2 focus:ring-[#6a0dad]"
                    }`}
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={isLoading}
                />
                {errors.password && (
                  <p className="mt-1 text-sm text-[#6a0dad]">{errors.password}</p>
                )}
              </div>

              {/* LOGIN BUTTON */}
              <button
                type="submit"
                disabled={isLoading}
                className={`w-full py-3 font-semibold tracking-wide text-white transition rounded-md shadow ${
                  isLoading
                    ? "bg-[#8a3dbd] cursor-not-allowed"
                    : "bg-[#6a0dad] hover:bg-[#3a066b]"
                }`}
              >
                {isLoading ? "SIGNING IN..." : "SIGN IN"}
              </button>

            </form>
          </div>
        </div>
      </div>

    </section>
  );
}